import { ReactNode, useEffect, useState } from 'react';
import { View, Text, TouchableOpacity } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { darkColors, lightColors, typography } from '../theme';
import { useTheme, useSidebar, useLanguage } from '../contexts/AppContext';
import { useAuth } from '../contexts/AuthContext';
import { dashboardService } from '../services/dashboard.service';
import {
  OverviewIcon,
  ChartIcon,
  ReportIcon,
  StarIcon,
  PerformanceIcon,
  TargetIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoonIcon,
  SunIcon,
  TrendingUpIcon,
  AnalystIcon,
  UserIcon,
  BellIcon,
  MessageIcon,
  RobotIcon,
  BookIcon,
} from './Icons';

interface SidebarItemProps {
  icon: ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  collapsed?: boolean;
}

function SidebarItem({ icon, label, active = false, onClick, collapsed = false }: SidebarItemProps) {
  const { isDark } = useTheme();
  const colors = isDark ? darkColors : lightColors;
  const [hovered, setHovered] = useState(false);
  const isHovered = hovered && !active;

  return (
    <TouchableOpacity
      onPress={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        padding: collapsed ? 12 : 14,
        borderRadius: parseInt(colors.borderRadius.regular),
        backgroundColor: active
          ? colors.surfaceElev
          : (isHovered ? (isDark ? 'rgba(31, 35, 39, 0.6)' : 'rgba(0, 0, 0, 0.05)') : 'transparent'),
        borderWidth: active ? 1 : 0,
        borderTopColor: active ? 'rgba(255,255,255,0.08)' : 'transparent',
        borderLeftColor: active ? 'rgba(255,255,255,0.05)' : 'transparent',
        borderRightColor: active ? (isDark ? 'rgba(0,0,0,0.25)' : 'rgba(0,0,0,0.1)') : 'transparent',
        borderBottomColor: active ? (isDark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.15)') : 'transparent',
        justifyContent: collapsed ? 'center' : 'flex-start',
        transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        cursor: 'pointer',
        boxShadow: active
          ? `0 2px 0 ${isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.08)'}, 0 4px 16px ${colors.accent}25, inset 0 1px 0 rgba(255,255,255,0.06)`
          : 'none',
        transform: active ? 'translateY(-1px)' : 'translateY(0)',
      } as any}
    >
      <View style={{ opacity: active ? 1 : 0.85 }}>
        {icon}
      </View>
      {!collapsed && (
        <Text style={{
          color: active ? colors.textPrimary : (isHovered ? colors.textPrimary : colors.textSecondary),
          fontSize: parseInt(typography.fontSize.default),
          fontWeight: active ? '600' : '500',
          fontFamily: typography.fontFamily.body,
          letterSpacing: -0.011,
        }}>
          {label}
        </Text>
      )}
    </TouchableOpacity>
  );
}

interface SidebarProps {
  children?: ReactNode;
}

export function Sidebar({ children }: SidebarProps) {
  const { isDark, toggleTheme } = useTheme();
  const { isCollapsed, toggleSidebar } = useSidebar();
  const { t } = useLanguage();
  const { user } = useAuth();
  const colors = isDark ? darkColors : lightColors;
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'ADMIN';
  const isAnalyst = user?.role === 'analyst' || user?.role === 'ANALYST';
  const hasClientData = !!(user as any)?.clientCode;
  const [allowedTabs, setAllowedTabs] = useState<string[]>([]);

  const iconColor = colors.textSecondary;
  const activeIconColor = colors.accent;

  // Fetch allowed tabs based on user subscription
  useEffect(() => {
    if (!isAdmin) {
      dashboardService.getHeader().then((data) => {
        if (data.allowedTabs) setAllowedTabs(data.allowedTabs);
      }).catch(() => { });
    }
  }, [isAdmin]);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const isActive = (path: string) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') return true;
    if (path !== '/dashboard' && location.pathname.startsWith(path)) return true;
    return false;
  };

  // Admins: admin sub-pages (Teams, Users, Notifications, Import, Plans, AI, Reviews). Analysts: only Review Requests. Viewers: full dashboard nav.
  const adminNavItems = [
    { path: '/dashboard/admin/teams', icon: TargetIcon, labelKey: 'nav.admin_teams' },
    { path: '/dashboard/admin/users', icon: UserIcon, labelKey: 'nav.admin_users' },
    { path: '/dashboard/admin/notifications', icon: BellIcon, labelKey: 'nav.admin_notifications' },
    { path: '/dashboard/admin/data', icon: ReportIcon, labelKey: 'nav.admin_data' },
    { path: '/dashboard/admin/leads', icon: MessageIcon, labelKey: 'nav.admin_leads' },
    { path: '/dashboard/admin/plans', icon: StarIcon, labelKey: 'nav.admin_plans' },
    { path: '/dashboard/admin/ai', icon: RobotIcon, labelKey: 'nav.admin_ai' },
    { path: '/dashboard/admin/reviews', icon: AnalystIcon, labelKey: 'nav.admin_reviews' },
  ];

  const handleOpenTechDocs = () => {
    navigate('/dashboard/admin/docs');
  };
  const navItems = isAdmin
    ? adminNavItems.map(({ path, icon: Icon, labelKey }) => ({
      path,
      icon: <Icon size={isMobile ? 24 : 20} color={isActive(path) ? activeIconColor : iconColor} />,
      label: t(labelKey),
    }))
    : isAnalyst
      ? [
        {
          path: '/dashboard/analyst',
          icon: <AnalystIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/analyst') ? activeIconColor : iconColor} />,
          label: t('nav.analyst_reviews'),
        },
        ...(hasClientData ? [{
          path: '/dashboard/my-data',
          icon: <ReportIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/my-data') ? activeIconColor : iconColor} />,
          label: t('nav.my_data') || 'My Data',
        }] : []),
      ]
      : [
        {
          path: '/dashboard',
          icon: <OverviewIcon size={isMobile ? 24 : 20} color={isActive('/dashboard') ? activeIconColor : iconColor} />,
          label: t('nav.overview'),
        },
        {
          path: '/dashboard/universe',
          icon: <ChartIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/universe') ? activeIconColor : iconColor} />,
          label: t('nav.cb_universe'),
        },
        {
          path: '/dashboard/aggregations',
          icon: <ReportIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/aggregations') ? activeIconColor : iconColor} />,
          label: t('nav.aggregations'),
        },
        {
          path: '/dashboard/performance',
          icon: <TrendingUpIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/performance') ? activeIconColor : iconColor} />,
          label: t('nav.performance'),
        },
        ...(hasClientData ? [{
          path: '/dashboard/my-data',
          icon: <ReportIcon size={isMobile ? 24 : 20} color={isActive('/dashboard/my-data') ? activeIconColor : iconColor} />,
          label: t('nav.my_data') || 'My Data',
        }] : []),
      ];

  // Map route paths to tab names for access gating (viewers only)
  const pathToTab: Record<string, string> = {
    '/dashboard': 'overview',
    '/dashboard/universe': 'universe',
    '/dashboard/aggregations': 'aggregations',
    '/dashboard/performance': 'performance',
  };

  // Filter nav items based on allowed tabs (skip for admin/analyst; for viewers skip if no plan = show all)
  const filteredNavItems = isAdmin || isAnalyst || allowedTabs.length === 0
    ? navItems
    : navItems.filter(item => {
      const tab = pathToTab[item.path];
      return !tab || allowedTabs.includes(tab);
    });

  // Mobile Bottom Navigation Bar
  if (isMobile) {
    return (
      <View
        className="mobile-bottom-nav"
        style={{
          position: 'fixed' as any,
          bottom: 0,
          left: 0,
          right: 0,
          height: 70,
          backgroundColor: colors.surface,
          borderTopWidth: 1,
          borderTopColor: colors.border,
          flexDirection: 'row',
          justifyContent: 'space-around',
          alignItems: 'center',
          zIndex: 1001,
          paddingBottom: 8,
          paddingTop: 8,
          paddingHorizontal: 8,
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        }}
      >
        {filteredNavItems.map((item) => (
          <TouchableOpacity
            key={item.path}
            onPress={() => navigate(item.path)}
            className="mobile-bottom-nav-item"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              flex: 1,
              padding: 8,
              borderRadius: 8,
              backgroundColor: isActive(item.path) ? colors.surfaceElev : 'transparent',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              maxWidth: 80,
            }}
          >
            <View style={{ opacity: isActive(item.path) ? 1 : 0.7 }}>
              {item.icon}
            </View>
            <Text
              className="mobile-bottom-nav-label"
              style={{
                fontSize: 10,
                fontWeight: isActive(item.path) ? '600' : '500',
                color: isActive(item.path) ? colors.textPrimary : colors.textSecondary,
                textAlign: 'center',
                whiteSpace: 'nowrap' as any,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                fontFamily: typography.fontFamily.body,
              }}
            >
              {item.label}
            </Text>
          </TouchableOpacity>
        ))}
        {isAdmin && (
          <TouchableOpacity
            onPress={handleOpenTechDocs}
            className="mobile-bottom-nav-item"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              flex: 1,
              padding: 8,
              borderRadius: 8,
              backgroundColor: 'rgba(99,102,241,0.08)',
              cursor: 'pointer',
              maxWidth: 80,
            }}
          >
            <BookIcon size={24} color="#818cf8" />
            <Text style={{
              fontSize: 10,
              fontWeight: '500',
              color: '#818cf8',
              textAlign: 'center',
              fontFamily: typography.fontFamily.body,
            }}>
              Docs
            </Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  // Desktop Sidebar (original)
  return (
    <View
      className="sidebar-desktop"
      style={{
        width: isCollapsed ? 80 : 280,
        backgroundColor: colors.surface,
        borderRightColor: colors.border,
        borderRightWidth: 1,
        padding: isCollapsed ? 16 : 24,
        gap: 28,
        height: '100vh',
        position: 'fixed' as any,
        left: 0,
        top: 0,
        zIndex: 1000,
        transition: 'all 0.4s ease',
        overflowY: 'auto' as any,
        overflowX: 'hidden' as any,
        borderTopRightRadius: parseInt(colors.borderRadius.large),
        borderBottomRightRadius: parseInt(colors.borderRadius.large),
      }}
    >
      {/* Header with Logo and Toggle */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        paddingTop: 4,
        marginBottom: 12,
      }}>
        {!isCollapsed && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
            <img
              src="/images/favicon.png"
              alt=""
              style={{ height: 32, width: 32, objectFit: 'contain' }}
            />
            <Text
              style={{
                color: colors.textPrimary,
                fontSize: parseInt(typography.fontSize.large),
                fontWeight: '100',
                fontFamily: '"Times New Roman", Times, serif',
                letterSpacing: 0.5,
              }}
            >
              CONVPILOT
            </Text>
          </View>
        )}
        {isCollapsed && (
          <img
            src="/images/favicon.png"
            alt="CONVPILOT"
            style={{ height: 28, width: 28, objectFit: 'contain' }}
          />
        )}
        <TouchableOpacity
          onPress={toggleSidebar}
          style={{
            padding: 8,
            borderRadius: parseInt(colors.borderRadius.small),
            backgroundColor: colors.surfaceCard,
            borderWidth: 1,
            borderColor: colors.border,
            transition: 'all 0.3s ease',
          }}
        >
          {isCollapsed ? (
            <ChevronRightIcon size={18} color={colors.textSecondary} />
          ) : (
            <ChevronLeftIcon size={18} color={colors.textSecondary} />
          )}
        </TouchableOpacity>
      </View>

      {/* Navigation Section */}
      <View style={{ gap: 6 }}>
        {filteredNavItems.map((item) => (
          <View
            key={item.path}
            nativeID={`walkthrough-sidebar-${item.path.replace(/\//g, '-').replace(/^-/, '')}`}
            style={{ gap: 6 }}
          >
            <SidebarItem
              icon={item.icon}
              label={item.label}
              active={isActive(item.path)}
              collapsed={isCollapsed}
              onClick={() => navigate(item.path)}
            />
          </View>
        ))}

        {/* Tech Docs — admin only */}
        {isAdmin && (
          <View style={{ marginTop: 8, paddingTop: 8, borderTopWidth: 1, borderTopColor: colors.border }}>
            <TouchableOpacity
              onPress={handleOpenTechDocs}
              onMouseEnter={(e: any) => {
                e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.12)' : 'rgba(99,102,241,0.08)';
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.4)';
              }}
              onMouseLeave={(e: any) => {
                e.currentTarget.style.backgroundColor = isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)';
                e.currentTarget.style.borderColor = 'rgba(99,102,241,0.2)';
              }}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: 12,
                padding: isCollapsed ? 12 : 14,
                borderRadius: parseInt(colors.borderRadius.regular),
                backgroundColor: isDark ? 'rgba(99,102,241,0.06)' : 'rgba(99,102,241,0.04)',
                borderWidth: 1,
                borderColor: 'rgba(99,102,241,0.2)',
                justifyContent: isCollapsed ? 'center' : 'flex-start',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              } as any}
            >
              <BookIcon size={isMobile ? 24 : 20} color="#818cf8" />
              {!isCollapsed && (
                <Text style={{
                  color: '#818cf8',
                  fontSize: parseInt(typography.fontSize.default),
                  fontWeight: '500',
                  fontFamily: typography.fontFamily.body,
                  letterSpacing: -0.011,
                }}>
                  Tech Docs
                </Text>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Footer: Dark Mode + Version */}
      <View style={{ marginTop: 'auto', gap: 12 }}>
        {/* Dark Mode Toggle */}
        <View style={{
          flexDirection: isCollapsed ? 'column' : 'row',
          alignItems: 'center',
          justifyContent: isCollapsed ? 'center' : 'space-between',
          padding: isCollapsed ? 8 : 14,
          backgroundColor: colors.surfaceCard,
          borderRadius: parseInt(colors.borderRadius.medium),
          borderWidth: 1,
          borderColor: colors.border,
          gap: isCollapsed ? 8 : 0,
        }}>
          {!isCollapsed && (
            <Text style={{
              color: colors.textPrimary,
              fontSize: parseInt(typography.fontSize.default),
              fontWeight: '500',
              fontFamily: typography.fontFamily.body
            }}>
              {isDark ? 'Dark' : 'Light'}
            </Text>
          )}
          <TouchableOpacity
            onPress={toggleTheme}
            style={{
              width: 48,
              height: 26,
              backgroundColor: isDark ? colors.accent : colors.accentCyan,
              borderRadius: parseInt(colors.borderRadius.rounded),
              padding: 3,
              alignItems: isDark ? 'flex-end' : 'flex-start',
              justifyContent: 'center',
              transition: 'all 0.4s ease',
            }}
          >
            <View style={{
              width: 20,
              height: 20,
              backgroundColor: colors.background,
              borderRadius: parseInt(colors.borderRadius.rounded),
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}>
              {isDark ? (
                <MoonIcon size={12} color={colors.accent} />
              ) : (
                <SunIcon size={12} color={colors.accentCyan} />
              )}
            </View>
          </TouchableOpacity>
        </View>

        {/* Version Number - under dark/light toggle */}
        <View style={{
          alignItems: isCollapsed ? 'center' : 'center',
          paddingHorizontal: isCollapsed ? 0 : 4,
        }}>
          <Text style={{
            color: colors.textMuted,
            textAlign: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: parseInt(typography.fontSize.xsmall),
            fontWeight: '600',
            fontFamily: typography.fontFamily.body,
            letterSpacing: 0.5,
            opacity: 0.7,
          }}>
            v1.0.0 - Pilantis © {new Date().getFullYear()}
          </Text>
        </View>
      </View>

      {children}
    </View>
  );
}
